import { MessageSquare, FileText } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Id } from '../../../../convex/_generated/dataModel';
import { useDiscussionQuestions } from '../../../../utils/convexHooks';
import { useExperimentStore } from '../store/experimentStore';

interface DiscussionQuestion {
    id: string;
    question: string;
    guidanceNotes?: string;
    maxWords?: number;
    expectedKeyPoints?: string[];
}

export default function DiscussionPanel() {
    const [searchParams] = useSearchParams();
    const idStr = searchParams.get('id');
    const experimentId = idStr ? (idStr as Id<"experimentConfigs">) : undefined;
    
    // Fetch discussion questions from the server
    const serverQuestions = useDiscussionQuestions(experimentId);
    
    const discussionAnswers = useExperimentStore((state) => state.discussionAnswers);
    const setDiscussionAnswer = useExperimentStore((state) => state.setDiscussionAnswer);

    // Transform server questions or use fallback empty array
    const discussionQuestions: DiscussionQuestion[] = serverQuestions ? serverQuestions.map((q: any) => ({
        id: q._id,
        question: q.question,
        guidanceNotes: q.modelAnswer ? "Consider comparing with key expected points." : undefined,
        expectedKeyPoints: q.expectedKeyPoints,
    })) : [];

    const handleAnswerChange = (questionId: string, value: string) => {
        setDiscussionAnswer(questionId, value);
    };

    const getWordCount = (text: string) => {
        return text.trim().split(/\s+/).filter(word => word.length > 0).length;
    };

    const answeredCount = Object.keys(discussionAnswers).filter(key => discussionAnswers[key]?.trim()).length;
    const totalQuestions = discussionQuestions.length || 0;

    return (
        <div className="flex flex-col gap-4 h-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600/10 to-purple-600/10 p-4 rounded-xl border border-indigo-500/20">
                <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    <h2 className="text-lg font-bold text-foreground">Post-Lab Discussion</h2>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                    Answer the following questions based on your experiment. Write detailed responses following Malaysian SPM practical format.
                </p>
                
                {/* Progress */}
                <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold text-foreground">Progress:</span>
                    <span className="px-2 py-1 bg-indigo-600/20 text-indigo-600 dark:text-indigo-400 rounded-md font-bold">
                        {answeredCount}/{totalQuestions} Questions Answered
                    </span>
                </div>
            </div>

            {/* Questions List */}
            <div className="flex-1 overflow-y-auto space-y-4 pr-2 customized-scrollbar">
                {discussionQuestions.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-4 text-center">No discussion questions available.</p>
                ) : discussionQuestions.map((question, index) => {
                    const answer = discussionAnswers[question.id] || '';
                    const wordCount = getWordCount(answer);
                    const isOverLimit = question.maxWords && wordCount > question.maxWords;

                    return (
                        <div
                            key={question.id}
                            className="bg-card border border-border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
                        >
                            {/* Question Header */}
                            <div className="flex items-start justify-between gap-2 mb-3">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-xs font-bold px-2 py-0.5 rounded bg-indigo-600/20 text-indigo-600 dark:text-indigo-400">
                                            Question {index + 1}
                                        </span>
                                    </div>
                                    <p className="text-sm font-medium text-foreground leading-relaxed">
                                        {question.question}
                                    </p>
                                </div>
                            </div>

                            {/* Guidance Notes */}
                            {question.guidanceNotes && (
                                <div className="mb-3 p-2 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                                    <div className="flex items-start gap-2">
                                        <MessageSquare className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                                        <p className="text-xs text-blue-600 dark:text-blue-400 leading-relaxed">
                                            <span className="font-semibold">Hint:</span> {question.guidanceNotes}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Answer Text Area */}
                            <div className="space-y-2">
                                <textarea
                                    value={answer}
                                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                    placeholder="Type your answer here..."
                                    className="w-full px-3 py-2 rounded-lg text-sm bg-muted border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-600 min-h-[120px] resize-y"
                                    rows={4}
                                />
                                
                                {/* Word Count & Actions */}
                                <div className="flex items-center justify-between">
                                    <div className="text-xs">
                                        {question.maxWords && (
                                            <span className={`font-medium ${isOverLimit ? 'text-red-600' : 'text-muted-foreground'}`}>
                                                {wordCount} / {question.maxWords} words
                                                {isOverLimit && ' (over limit!)'}
                                            </span>
                                        )}
                                        {!question.maxWords && (
                                            <span className="text-muted-foreground font-medium">
                                                {wordCount} words
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
